import { Checkbox, Chip } from '@mui/material';
import {
  difference,
  every,
  filter,
  find,
  isEmpty,
  map,
  union,
  without,
} from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import { payoutManualApi, shopsApi, usersApi } from 'api';
import {
  CloseDialogResult,
  DataGridColumnDefinition,
  PaginationWrapper,
} from 'components';
import { MANUAL_PAYOUT_STATUS_DETAILS } from 'constants/order.constants';
import {
  BULK_DEFAULT_PAGINATION,
  BULK_ROWS_PER_PAGE_OPTIONS,
} from 'constants/pagination.constants';
import { TIME_INTERVAL } from 'constants/time-interval.constants';
import {
  AdditionalOrderColumn,
  FilterDefinitionType,
  OrdersTab,
  QueryKey,
} from 'enums';
import {
  useCurrencies,
  useMutation,
  useOrderQueryFilters,
  usePartialQuery,
  usePayoutOrdersContext,
  useQueryFilters,
  useShopsQuery,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  PayoutManualBatch,
  PayoutManualBatchStatus,
  PayoutOrder,
  PayoutOrderFilters,
} from 'types';
import { formUtils, orderUtils, requisitesUtils } from 'utils';

import { AssignProviderDialog } from './AssignProviderDialog';
import { BatchResultDialog } from './BatchResultDialog';
import { CancelOrdersDialog } from './CancelOrdersDialog';
import { ManualOrdersToolbar } from './ManualOrdersToolbar';
import { OrderList } from '../OrderList';
import { OrdersActions } from '../OrdersActions';

export const ManualOrders: React.FC = () => {
  const { manualSelectedIds, setManualSelectedIds } = usePayoutOrdersContext();
  useOrderQueryFilters(
    manualSelectedIds?.length ? { from: undefined } : undefined,
  );

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders',
  });
  const { role } = useUser();
  const { filters, setFilters } = useQueryFilters<PayoutOrderFilters>();
  const { paymentTypes, banks } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();
  const queryClient = useQueryClient();

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  // The batch is processed in the background, so the dialog follows it by id until it completes.
  const [runningBatchId, setRunningBatchId] = useState<string | null>(null);

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );
  const queryResultMerchants = useQuery(
    QueryKey.UsersMerchants,
    usersApi.getAllMerchants,
  );

  const queryResult = usePartialQuery(
    QueryKey.ManualPayoutOrders,
    payoutManualApi.getQueuePaginated,
    { useCachedCount: true, defaultPagination: BULK_DEFAULT_PAGINATION },
  );

  const items: PayoutOrder[] = useMemo(
    () => queryResult.data?.items || [],
    [queryResult.data],
  );
  // An order locked by someone else's batch is already being processed: it is shown as busy and
  // kept out of every selection, so the operator never picks one only to see it skipped later.
  const filteredSelectableIds = useMemo(
    () =>
      map(
        filter(items, (item) => !item.lock),
        'id',
      ),
    [items],
  );

  const selectedIds = useMemo(
    () => manualSelectedIds || [],
    [manualSelectedIds],
  );

  const isPageSelected = useMemo(
    () =>
      !isEmpty(filteredSelectableIds) &&
      every(filteredSelectableIds, (id) => selectedIds.includes(id)),
    [filteredSelectableIds, selectedIds],
  );

  // Selecting the whole page may push the selection over the batch cap. Silently trimming it back
  // would leave the header checkbox un-checking itself for no visible reason, so the extra orders
  // stay selected and the toolbar says outright that the operation is blocked until the cap is met.
  const handleTogglePage = useCallback(() => {
    setManualSelectedIds?.(
      isPageSelected
        ? difference(selectedIds, filteredSelectableIds)
        : union(selectedIds, filteredSelectableIds),
    );
  }, [
    isPageSelected,
    filteredSelectableIds,
    selectedIds,
    setManualSelectedIds,
  ]);

  const handleToggleOrder = useCallback(
    (orderId: string) =>
      setManualSelectedIds?.(
        selectedIds.includes(orderId)
          ? without(selectedIds, orderId)
          : [...selectedIds, orderId],
      ),
    [selectedIds, setManualSelectedIds],
  );

  const handleClearSelection = useCallback(
    () => setManualSelectedIds?.([]),
    [setManualSelectedIds],
  );

  const selectionColumn: DataGridColumnDefinition<PayoutOrder> = useMemo(
    () => ({
      header: (
        <Checkbox
          size="small"
          checked={isPageSelected}
          disabled={isEmpty(filteredSelectableIds)}
          onChange={handleTogglePage}
        />
      ),
      valueGetter: (item) =>
        item.lock ? (
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label={
              item.lock.user?.name
                ? t('manual.busy_by', { name: item.lock.user.name })
                : t('manual.busy')
            }
          />
        ) : (
          <Checkbox
            size="small"
            checked={selectedIds.includes(item.id)}
            onChange={() => handleToggleOrder(item.id)}
          />
        ),
    }),
    [
      isPageSelected,
      filteredSelectableIds,
      handleTogglePage,
      selectedIds,
      handleToggleOrder,
      t,
    ],
  );

  const columns = useMemo(
    () => [
      selectionColumn,
      ...orderUtils.getPayoutOrderColumns(
        [
          AdditionalOrderColumn.CustomerContacts,
          AdditionalOrderColumn.Merchant,
          AdditionalOrderColumn.StatusActive,
        ],
        { role },
      ),
    ],
    [selectionColumn, role],
  );

  const additionalFilters: FilterDefinition<PayoutOrderFilters>[] = useMemo(
    () => [
      {
        label: t('filters.shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        shops: queryResultShops.data,
      },
      {
        label: t('filters.merchant'),
        name: 'merchantId',
        type: FilterDefinitionType.User,
        users: queryResultMerchants.data,
      },
      {
        label: t('filters.external_order_id'),
        name: 'externalOrderId',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.order_ids'),
        name: 'orderIdsConcatenated',
        type: FilterDefinitionType.TextArea,
      },
      {
        label: t('filters.customer_phone'),
        name: 'customerPhone',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.customer_card'),
        name: 'customerCard',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: t('filters.bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: t('filters.fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
      {
        label: t('filters.status'),
        name: 'statusDetails',
        type: FilterDefinitionType.Select,
        options: map(MANUAL_PAYOUT_STATUS_DETAILS, (value) => ({
          value,
          label: t(`manual.status_details.${value}` as any),
        })),
        getDisplayName: (value: string) =>
          t(`manual.status_details.${value}` as any),
      },
    ],
    [
      t,
      queryResultShops.data,
      queryResultMerchants.data,
      paymentTypes,
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
    ],
  );

  const handleBatchStarted = useCallback(
    (batch: PayoutManualBatch) => {
      setRunningBatchId(batch.id);
      setManualSelectedIds?.([]);
    },
    [setManualSelectedIds],
  );

  const batchQueryResult = useQuery(
    [QueryKey.PayoutManualBatch, runningBatchId],
    () => payoutManualApi.getBatch(runningBatchId as string),
    {
      enabled: !!runningBatchId,
      refetchInterval: (data) =>
        data?.status === PayoutManualBatchStatus.Completed
          ? false
          : TIME_INTERVAL.TWO_SECONDS,
      onSuccess: (data) => {
        if (data?.status === PayoutManualBatchStatus.Completed) {
          void queryClient.invalidateQueries(QueryKey.ManualPayoutOrders);
          void queryClient.invalidateQueries(QueryKey.PayoutManualBatches);
        }
      },
    },
  );

  const { mutate: assignOrders, isLoading: isAssigning } = useMutation<
    PayoutManualBatch,
    unknown,
    { orderIds: string[]; providerId: string }
  >(payoutManualApi.assign, {
    mergeNotifierMessages: { success: null },
    onSuccess: handleBatchStarted,
  });

  const { mutate: cancelOrders, isLoading: isCancelling } = useMutation<
    PayoutManualBatch,
    unknown,
    { orderIds: string[] }
  >(payoutManualApi.cancel, {
    mergeNotifierMessages: { success: null },
    onSuccess: handleBatchStarted,
  });

  const handleAssignDialogOpen = useCallback(
    () => setAssignDialogOpen(true),
    [],
  );
  const handleCancelDialogOpen = useCallback(
    () => setCancelDialogOpen(true),
    [],
  );

  const handleAssignDialogClose = useCallback(
    (result: CloseDialogResult<{ providerId: string }>) => {
      setAssignDialogOpen(false);
      if (result.ok && result.data?.providerId) {
        assignOrders({
          orderIds: selectedIds,
          providerId: result.data.providerId,
        });
      }
    },
    [assignOrders, selectedIds],
  );

  const handleCancelDialogClose = useCallback(
    (result: CloseDialogResult) => {
      setCancelDialogOpen(false);
      if (result.ok) {
        cancelOrders({ orderIds: selectedIds });
      }
    },
    [cancelOrders, selectedIds],
  );

  const handleResultClose = useCallback(() => setRunningBatchId(null), []);

  // Re-selects exactly the orders that did not go through and narrows the queue to them, so the
  // operator does not have to hunt them down across pages.
  const handleRetryFailed = useCallback(
    (orderIds: string[]) => {
      setRunningBatchId(null);
      setManualSelectedIds?.(orderIds);
      setFilters({
        orderIdsConcatenated: orderIds.join(','),
      } as PayoutOrderFilters);
    },
    [setFilters, setManualSelectedIds],
  );

  return (
    <Fragment>
      <OrdersActions
        tab={OrdersTab.ManualQueue}
        hideExport
        queryResult={queryResult}
        sortingFields={orderUtils.getCommonPayoutOrderSortingFields()}
        filtersDefinitions={[
          ...orderUtils.getCommonPayoutOrderFilters(filters),
          ...additionalFilters,
        ]}
      />
      {!isEmpty(items) && (
        <ManualOrdersToolbar
          selectedCount={selectedIds.length}
          onAssign={handleAssignDialogOpen}
          onCancel={handleCancelDialogOpen}
          onClear={handleClearSelection}
        />
      )}
      <PaginationWrapper
        queryResult={queryResult}
        rowsPerPageOptions={BULK_ROWS_PER_PAGE_OPTIONS}
      >
        <OrderList items={items as never} columns={columns as never} />
      </PaginationWrapper>
      <AssignProviderDialog
        open={assignDialogOpen && !isAssigning}
        selectedCount={selectedIds.length}
        onClose={handleAssignDialogClose}
      />
      <CancelOrdersDialog
        open={cancelDialogOpen && !isCancelling}
        selectedCount={selectedIds.length}
        onClose={handleCancelDialogClose}
      />
      <BatchResultDialog
        open={!!runningBatchId}
        batch={batchQueryResult.data}
        onClose={handleResultClose}
        onRetryFailed={handleRetryFailed}
      />
    </Fragment>
  );
};
